import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DownloadSelectionFactory,
  type Asset,
  type DownloadSelection,
} from 'app/domain';

export type DownloadSelectionContextValue = {
  selection: DownloadSelection;
  toggle: (asset: Asset) => void;
  selectAll: (assets: readonly Asset[]) => void;
  clear: () => void;
};

export const DownloadSelectionContext =
  createContext<DownloadSelectionContextValue | null>(null);

const factory = new DownloadSelectionFactory();

// Takes no search input, so the selection deliberately survives a new search.
export const DownloadSelectionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selection, setSelection] = useState(factory.empty);

  const toggle = useCallback(
    (asset: Asset) => setSelection((current) => current.toggle(asset)),
    []
  );
  const selectAll = useCallback(
    (assets: readonly Asset[]) =>
      setSelection((current) => current.add(assets)),
    []
  );
  const clear = useCallback(
    () => setSelection((current) => current.cleared()),
    []
  );

  const value = useMemo(
    () => ({ selection, toggle, selectAll, clear }),
    [selection, toggle, selectAll, clear]
  );

  return (
    <DownloadSelectionContext.Provider value={value}>
      {children}
    </DownloadSelectionContext.Provider>
  );
};
